import { getAiAgentMissionNumber, getAiAgentWsUrl, onAiAgentMissionChange } from "./aiAgentConfig";
import { AiAgentResultTracker } from "./aiAgentResults";
import { hideAgentIndicator, showAgentIndicator } from "./aiAgentIndicator";
import { claimAgentSession } from "./aiAgentSession";

// Caps in-flight decisions tracked, so a dropped `command` reply can't leak memory.
const MAX_PENDING_DECISIONS = 50;
// Opportunity bookkeeping is tiny (strings), so it keeps a longer window than the full
// snapshots above - a slow LLM reply still has to be checked against the choice it answered.
const MAX_TRACKED_OPPORTUNITIES = 200;

function rememberBounded(collection, key, value) {
  if (collection instanceof Map) collection.set(key, value);
  else collection.add(key);
  if (collection.size > MAX_TRACKED_OPPORTUNITIES) collection.delete(collection.keys().next().value);
}

/** Real WebSocket wiring for the aiAgent layer - see README.md's "The WebSocket protocol". */
export class AiAgentSocket {
  constructor({ session, stateReader, pressLane, pressKey, wsUrl = getAiAgentWsUrl(), logMessages = true }) {
    this.session = session;
    this.stateReader = stateReader;
    this.pressLane = pressLane;
    this.pressKey = pressKey;
    this.wsUrl = wsUrl;
    this.logMessages = logMessages;
    this.ws = null;
    this.ready = false;
    this.unsubscribeMissionChange = null;
    this.socketMissionNumber = null; // mission the open socket's session.start named
    this.unsubscribeStateReader = null;
    this.pendingDecisions = new Map(); // decisionId -> snapshot sent, for gameStateAtDecision on the reply
    this.decisionChoices = new Map(); // decisionId -> { opportunityId, kind, onsetMs } of the choice it was sent for
    this.appliedOpportunities = new Set(); // opportunities a command was already applied to
    this.decisionCounter = 0;
    this.results = new AiAgentResultTracker({
      send: (message) => this.send({ ...message, agentRunId: this.session.agentRunId }),
      logMessages,
    });
  }

  /**
   * Opens the session once the running mission is known - the agent needs it on session.start,
   * and online builds only learn it when a mission's timeline starts. When a later mission
   * starts, the session is reopened for it, since the agent builds a mission-specific agent per
   * session.
   */
  connect() {
    if (!this.unsubscribeMissionChange) {
      this.unsubscribeMissionChange = onAiAgentMissionChange((missionNumber) => {
        // e.g. dev:0 already connected for mission 0 from the env, before its timeline started.
        if (this.ws && this.socketMissionNumber === missionNumber) return;
        if (this.ws) {
          if (this.logMessages) console.log(`[aiAgentSocket] mission ${missionNumber} started - reopening the session`);
          this.disconnect();
        }
        this.openSocket();
      });
    }
    if (getAiAgentMissionNumber() === null) {
      if (this.logMessages) console.log("[aiAgentSocket] waiting for a mission to start before connecting");
      return;
    }
    this.openSocket();
  }

  openSocket() {
    if (this.ws) return;

    const ws = new WebSocket(this.wsUrl);
    this.ws = ws;
    // A declined session stops the reader; a later mission's session needs it running again.
    this.stateReader.start();

    this.socketMissionNumber = getAiAgentMissionNumber();

    ws.addEventListener("open", () => {
      if (this.logMessages) console.log("[aiAgentSocket] connected ->", this.wsUrl, "- sending session.start");
      ws.send(JSON.stringify({ type: "session.start", missionNumber: this.socketMissionNumber }));
    });

    ws.addEventListener("message", (event) => this.handleMessage(event));

    ws.addEventListener("close", () => {
      if (this.logMessages) console.log("[aiAgentSocket] disconnected");
      // A socket closed to reopen for a new mission must not tear down its replacement.
      if (this.ws === ws) this.teardown();
    });

    ws.addEventListener("error", () => {
      console.warn(
        "[aiAgentSocket] socket error - is the agent repo (or the local simulator) reachable at",
        this.wsUrl,
        "?"
      );
    });
  }

  handleMessage(event) {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      console.warn("[aiAgentSocket] ignoring non-JSON socket message", event.data);
      return;
    }

    if (message.type === "session.ready") {
      // agentSession: false (or missing) = agent declined; close and stay un-agented.
      if (message.agentSession !== true) {
        console.warn("[aiAgentSocket] session.ready declined agentSession - closing, staying un-agented", message);
        this.stateReader.stop();
        this.disconnect();
        return;
      }
      try {
        claimAgentSession(this.session, message);
      } catch (err) {
        console.warn("[aiAgentSocket] session.ready rejected -", err.message, message);
        return;
      }
      this.ready = true;
      showAgentIndicator();
      if (this.logMessages) {
        console.log("[aiAgentSocket] session.ready - agent declared itself, state streaming starts now", this.session);
      }
      this.unsubscribeStateReader = this.stateReader.subscribe((snapshot) => {
        this.sendState(snapshot);
        this.results.observe(this.stateReader.getGame());
      });
      return;
    }

    if (message.type === "command") {
      this.handleCommand(message);
      return;
    }

    if (message.type === "error") {
      console.warn("[aiAgentSocket] agent reported an error", message);
      return;
    }

    if (this.logMessages) console.log("[aiAgentSocket] unhandled socket message", message);
  }

  handleCommand(message) {
    const gameStateAtDecision = this.pendingDecisions.get(message.decisionId) ?? null;
    this.pendingDecisions.delete(message.decisionId);
    const { lane, code } = message.action ?? {};
    if (this.logMessages) {
      console.log(
        `[aiAgentSocket] command received - ${lane != null ? `lane ${lane} (${code})` : code}, decisionLatencyMs=${message.decisionLatencyMs}, decisionId=${message.decisionId}`
      );
    }

    // A command answers whichever choice was open when its state was sent. If the game has since
    // moved on to a different choice, applying it now would act on a choice nobody actually made -
    // so it's dropped, the same way a keypress is ignored once nothing is listening for it anymore.
    const choice = this.decisionChoices.get(message.decisionId) ?? null;
    const currentOpportunityId = this.stateReader.getSnapshot()?.actionOpportunityId ?? null;
    if (choice || currentOpportunityId) {
      if (!choice || choice.opportunityId !== currentOpportunityId) {
        // An attend reply that missed its window still gets reported if the driver crashed while
        // it was in flight - otherwise the agent never learns why that decision got no response.
        const resolvedAsCrash = this.results.resolveStaleAttend({
          decisionId: message.decisionId,
          action: message.action,
          kind: choice?.kind ?? null,
          game: this.stateReader.getGame(),
        });
        if (!resolvedAsCrash) {
          console.warn("[aiAgentSocket] dropping stale command - its choice is no longer open", {
            decisionId: message.decisionId,
            commandOpportunityId: choice?.opportunityId ?? null,
            currentOpportunityId,
          });
        }
        return;
      }
      if (this.appliedOpportunities.has(choice.opportunityId)) {
        if (this.logMessages) {
          console.log("[aiAgentSocket] ignoring repeated command for an already-applied choice", message.decisionId);
        }
        return;
      }
      rememberBounded(this.appliedOpportunities, choice.opportunityId);
    }

    const pressOptions = {
      decisionId: message.decisionId,
      decisionLatencyMs: message.decisionLatencyMs,
      gameStateAtDecision,
    };
    // lane is only present for lane presses; anything else (e.g. Space) is a bare key press.
    // Returns when the key was dispatched - the action's application time.
    const press = () => {
      const appliedMs = performance.now();
      if (lane != null) this.pressLane(lane, pressOptions);
      else if (code) this.pressKey(code, pressOptions);
      return appliedMs;
    };
    this.results.apply({
      decisionId: message.decisionId,
      action: message.action,
      kind: choice?.kind ?? null,
      choiceOnsetMs: choice?.onsetMs ?? null,
      game: this.stateReader.getGame(),
      press,
    });
  }

  sendState(snapshot) {
    if (!this.ready || !snapshot || this.ws?.readyState !== WebSocket.OPEN) return;

    // session/capturedAt move from inside the snapshot to the envelope, per README.
    // eslint-disable-next-line no-unused-vars
    const { session, capturedAt, ...state } = snapshot;
    const decisionId = `${this.session.agentRunId}-${String(this.decisionCounter++).padStart(6, "0")}`;

    this.pendingDecisions.set(decisionId, snapshot);
    if (this.pendingDecisions.size > MAX_PENDING_DECISIONS) {
      this.pendingDecisions.delete(this.pendingDecisions.keys().next().value);
    }
    if (state.actionOpportunityId) {
      rememberBounded(this.decisionChoices, decisionId, {
        opportunityId: state.actionOpportunityId,
        kind: { attend: "attend", hq_message: "hq_message" }[state.agentObservation?.phase] ?? "choice",
        onsetMs: this.stateReader.getChoiceOnsetMs(state.actionOpportunityId),
      });
    }

    this.send({ type: "state", agentRunId: this.session.agentRunId, decisionId, capturedAt, state });
  }

  send(message) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(message));
  }

  teardown() {
    if (this.unsubscribeStateReader) this.unsubscribeStateReader();
    this.unsubscribeStateReader = null;
    this.ready = false;
    hideAgentIndicator();
    this.ws = null;
    this.pendingDecisions.clear();
    this.decisionChoices.clear();
    this.appliedOpportunities.clear();
    this.results.reset();
  }

  disconnect() {
    this.ws?.close();
    this.teardown();
  }
}
